import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import SignupStepHeader from './SignupStepHeader';
import ContactInfoStep from './steps/ContactInfoStep';
import OrganizationStep from './steps/OrganizationStep';
import IndustryDetailsStep from './steps/IndustryDetailsStep';
import RiskModulesStep from './steps/RiskModulesStep';
import TeamSetupStep from './steps/TeamSetupStep';
import { SIGNUP_STEPS, getStepByKey, getNextStep, getPreviousStep } from './signupStepConfig';
import LoadingSpinner from '../LoadingSpinner';
import SignupTermsAcceptance from './SignupTermsAcceptance';
import { useGoogleMapsLoader } from '../../hooks/useGoogleMapsLoader';
import { defaultRegionId } from '../../utils/regions';

// Unambiguous alphanumeric characters for the image CAPTCHA.
// Excludes visually similar glyphs: 0/O, 1/I/l, 5/S, 8/B, Z/2.
const CAPTCHA_CHARSET = 'ABCDEFGHJKLMNPQRTUVWXY346789';
const CAPTCHA_LENGTH  = 6;

// Six dark-navy hues — varied just enough to add visual interest.
const CAPTCHA_CHAR_COLORS = [
  '#1a3a5c', '#243068', '#1e3c62', '#2a3870', '#163254', '#1c2e58',
];

// Build per-character rendering data (rotation, y-jitter, size, color).
// Pre-generated so the SVG is stable across React re-renders.
const buildCharData = () =>
  Array.from({ length: CAPTCHA_LENGTH }, (_, i) => ({
    rotation : (Math.random() - 0.5) * 38,          // –19° … +19°
    yOffset  : (Math.random() - 0.5) * 14,          // –7 … +7 px
    fontSize : 21 + Math.random() * 7,              // 21 … 28 px
    colorIdx : i % CAPTCHA_CHAR_COLORS.length,
  }));

// Five random noise lines across the canvas (W=240, H=72).
const buildNoiseLines = () =>
  Array.from({ length: 5 }, () => ({
    x1: Math.random() * 240, y1: Math.random() * 72,
    x2: Math.random() * 240, y2: Math.random() * 72,
  }));

// Full CAPTCHA generator — call once on mount and on each refresh.
const generateCaptcha = () => {
  let chars = '';
  for (let i = 0; i < CAPTCHA_LENGTH; i++) {
    chars += CAPTCHA_CHARSET[Math.floor(Math.random() * CAPTCHA_CHARSET.length)];
  }
  return {
    captchaQuestion : chars,                   // rendered in SVG image
    captchaExpected : chars.toUpperCase(),     // compared case-insensitively
    captchaCharData : buildCharData(),
    captchaNoiseLines: buildNoiseLines(),
    captchaAnswer   : '',
  };
};

const Signup = () => {
  const navigate = useNavigate();

  // Start loading the Google Maps script immediately when the wizard mounts
  // so it is ready (or close to ready) by the time the user reaches step 1.
  useGoogleMapsLoader();

  // Organization is now step 1
  const [currentStepKey, setCurrentStepKey] = useState('organization');

  // Generate the initial CAPTCHA once (stable across re-renders via lazy init)
  const [formData, setFormData] = useState(() => ({
    // Step 1: Organization Info
    organizationName: '',
    companySize: '',
    annualRevenue: '',
    organizationAddress1: '',
    organizationCity: '',
    organizationState: '',
    organizationZip: '',
    organizationCountry: '',
    regionId: '',
    // Step 2: Contact Info
    userFirstName: '',
    userLastName: '',
    userDomain: '',
    userEmail: '',
    organizationContactNo1: '',
    // Step 3: Industry Details
    verticalId: '',
    subVerticalId: '',
    // Step 4: Team invites
    teamInvites: [{ firstName: '', lastName: '', domain: '', contactNumber: '', email: '', role: 'OWNER' }],
    // Step 5: Risk Modules & Referral
    licensedProducts: [],
    referralSource: '',
    // CAPTCHA — all visual + validation fields generated together
    ...generateCaptcha(),
  }));

  // Validation errors per field
  const [errors, setErrors] = useState({});

  // API data
  const [regions, setRegions] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Terms & Conditions overlay
  const [showTerms, setShowTerms] = useState(false);

  // Shown when the backend flags a possible duplicate organization and asks to confirm.
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');
  // 'wizard' = prompted mid-flow (Continue advances a step); 'submit' = prompted at final submit.
  const [duplicatePromptMode, setDuplicatePromptMode] = useState('wizard');
  // Set once the user acknowledges the duplicate warning, so we don't ask again on submit.
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);

  // Async address validation state (used during organization step advance)
  const [validating, setValidating] = useState(false);

  // Ref that holds the async validator registered by OrganizationStep.
  const orgStepAsyncValidateRef = useRef(null);

  // Fetch regions and verticals on mount (public API call)
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setDataLoading(true);
        const [regionsRes, verticalsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/Regions`),
          axios.get(`${API_BASE_URL}/Verticals`)
        ]);
        const loadedRegions = regionsRes.data ?? [];
        setRegions(loadedRegions);
        // Only configured regions are listed; with one, the picker is hidden and it is used.
        setFormData(prev => ({ ...prev, regionId: defaultRegionId(loadedRegions, prev.regionId) }));
        setVerticals(verticalsRes.data);
      } catch (err) {
        setDataError('Failed to load form data. Please refresh the page.');
      } finally {
        setDataLoading(false);
      }
    };
    fetchDropdownData();
  }, []);

  // Handle field change
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'verticalId') {
      setFormData(prev => ({ ...prev, verticalId: value, subVerticalId: '' }));
    } else if (name === 'regionId') {
  setFormData(prev => ({ ...prev, regionId: value }));
    } else if (name === 'organizationCountry') {
      setFormData(prev => ({ ...prev, organizationCountry: value, organizationState: '' }));
    } else if (
      name === 'organizationZip' ||
      name === 'organizationCity' ||
      name === 'organizationState'
    ) {
      setFormData(prev => ({ ...prev, [name]: value, organizationAddress1: '' }));
    } else if (name === 'userDomain') {
      // Domain changed — clear email so user re-enters one matching the new domain
      setFormData(prev => ({ ...prev, userDomain: value, userEmail: '' }));
      setErrors(prev => ({ ...prev, userDomain: null, userEmail: null }));
      return;
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Batch-update multiple form fields at once — used by Places Autocomplete
  const handleBatchChange = useCallback((fields) => {
    setFormData((prev) => {
      const updates = {};
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined) updates[k] = v;
      });
      return { ...prev, ...updates };
    });
    setErrors((prev) => {
      const cleared = { ...prev };
      Object.keys(fields).forEach((k) => {
        if (fields[k] !== undefined) cleared[k] = null;
      });
      return cleared;
    });
  }, []);

  // Handle checkbox/array field changes
  const handleCheckboxChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Check email domain on blur — fires when the user leaves the email field
  const handleEmailBlur = (e) => {
    const value = e.target.value.trim();
    const atIdx = value.indexOf('@');
    if (atIdx === -1) return; // incomplete — let required validation handle it
    const emailDomain = value.slice(atIdx + 1).toLowerCase();
    const orgDomain   = (formData.userDomain || '').toLowerCase().trim();
    if (emailDomain && orgDomain && emailDomain !== orgDomain) {
      alert(`Email must belong to the domain "${formData.userDomain}". Please enter a valid address.`);
      setFormData(prev => ({ ...prev, userEmail: '' }));
      setErrors(prev => ({ ...prev, userEmail: null }));
    }
  };

  // Regenerate the CAPTCHA image (new characters + new visual data)
  const handleRefreshCaptcha = () => {
    setFormData(prev => ({ ...prev, ...generateCaptcha() }));
    setErrors(prev => ({ ...prev, captchaAnswer: null }));
  };

  // Compute subVerticals from selected vertical
  const selectedVertical = verticals.find(v => v.verticalId === parseInt(formData.verticalId));
  const subVerticals = selectedVertical?.subVerticals || [];

  // Validate current step and proceed
  const handleNext = async () => {
    const currentStep = getStepByKey(currentStepKey);

    const syncValidation = currentStep.validate(formData);
    if (!syncValidation.isValid) {
      setErrors(syncValidation.errors);
      return;
    }

    // For the organization step, run async geocoding address validation
    if (currentStepKey === 'organization' && orgStepAsyncValidateRef.current) {
      setValidating(true);
      try {
        const asyncResult = await orgStepAsyncValidateRef.current(formData);
        if (!asyncResult.isValid) {
          setErrors(asyncResult.errors);
          return;
        }
      } finally {
        setValidating(false);
      }
    }

    // For the contact step, check that the email domain is not a public/consumer domain
    if (currentStepKey === 'contactInfo' && formData.userEmail) {
      const atIdx = formData.userEmail.indexOf('@');
      const emailDomain = atIdx !== -1 ? formData.userEmail.slice(atIdx + 1).toLowerCase().trim() : '';
      if (emailDomain) {
        setValidating(true);
        try {
          const res = await axios.get(`${API_BASE_URL}/signup/check-domain?domain=${encodeURIComponent(emailDomain)}`);
          if (res.data.isPublic) {
            setErrors(prev => ({
              ...prev,
              userEmail: 'Public email domains are not allowed. Please use your business email address.'
            }));
            return;
          }
        } catch {
          // If check fails, allow the user to proceed (fail-open)
        } finally {
          setValidating(false);
        }
      }
    }

    // After the contact step we have both org name + domain, so warn early about a
    // likely-duplicate organization before the user completes the rest of the flow.
    if (currentStepKey === 'contactInfo' && !duplicateConfirmed) {
      setValidating(true);
      try {
        const params = new URLSearchParams();
        if (formData.organizationName) params.set('name', formData.organizationName);
        if (formData.userDomain) params.set('domain', formData.userDomain);
        const res = await axios.get(`${API_BASE_URL}/signup/check-duplicate?${params.toString()}`);
        if (res.data.isDuplicate) {
          setDuplicateMessage(res.data.message);
          setDuplicatePromptMode('wizard');
          setShowDuplicatePrompt(true);
          return; // block advancing until the user decides
        }
      } catch {
        // If the check fails, allow the user to proceed (fail-open)
      } finally {
        setValidating(false);
      }
    }

    advanceFrom(currentStepKey);
  };

  // Advance to the next wizard step, seeding the first team row's domain when entering team setup.
  const advanceFrom = (stepKey) => {
    const nextStep = getNextStep(stepKey);
    if (nextStep) {
      if (nextStep.key === 'teamSetup') {
        setFormData(prev => ({
          ...prev,
          teamInvites: prev.teamInvites.map((r, i) =>
            i === 0 && !r.domain ? { ...r, domain: prev.userDomain || '' } : r
          ),
        }));
      }
      setCurrentStepKey(nextStep.key);
      setErrors({});
    }
  };

  // User acknowledged (or dismissed) the possible-duplicate warning.
  const handleDuplicateContinue = () => {
    setDuplicateConfirmed(true);
    setShowDuplicatePrompt(false);
    if (duplicatePromptMode === 'submit') {
      submitSignup(true);
    } else {
      advanceFrom('contactInfo');
    }
  };

  const handleBack = () => {
    const prevStep = getPreviousStep(currentStepKey);
    if (prevStep) {
      setCurrentStepKey(prevStep.key);
      setErrors({});
    }
  };

  // ── Team invite row handlers ────────────────────────────────────────────
  const handleInviteChange = (idx, field, value) => {
    setFormData(prev => {
      const rows = [...prev.teamInvites];
      rows[idx] = { ...rows[idx], [field]: value };
      // Auto-populate email placeholder domain when domain changes (no forced rewrite)
      return { ...prev, teamInvites: rows };
    });
  };

  const handleAddInvite = () => {
    setFormData(prev => ({
      ...prev,
      teamInvites: [
        ...prev.teamInvites,
        { firstName: '', lastName: '', domain: prev.userDomain || '', contactNumber: '', email: '', role: 'OWNER' },
      ],
    }));
  };

  const handleRemoveInvite = (idx) => {
    setFormData(prev => ({
      ...prev,
      teamInvites: prev.teamInvites.filter((_, i) => i !== idx),
    }));
  };

  const handleSkipTeam = () => {
    setFormData(prev => ({
      ...prev,
      teamInvites: [{ firstName: '', lastName: '', domain: prev.userDomain || '', contactNumber: '', email: '', role: 'OWNER' }],
    }));
    setErrors({});
    const next = getNextStep('teamSetup');
    if (next) setCurrentStepKey(next.key);
  };

  // Validate last step and show T&C overlay
  const handleSubmit = () => {
    const currentStep = getStepByKey(currentStepKey);
    const validation = currentStep.validate(formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setShowTerms(true);
  };

  // Called after user accepts T&C — performs the actual API call
  const submitSignup = async (confirmDuplicate = false) => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const signupDto = {
        organizationName: formData.organizationName,
        companySize: formData.companySize || null,
        annualRevenue: formData.annualRevenue || null,
        organizationAddress1: formData.organizationAddress1,
        organizationCity: formData.organizationCity || null,
        organizationState: formData.organizationState || null,
        organizationPostalCode: formData.organizationZip || null,
        organizationCountry: formData.organizationCountry,
        regionId: formData.regionId ? Number(formData.regionId) : null,
        verticalId: Number(formData.verticalId),
        subVerticalId: Number(formData.subVerticalId),
        userEmail: formData.userEmail,
        userFirstName: formData.userFirstName,
        userLastName: formData.userLastName,
        userDomain: formData.userDomain || null,
        organizationContactNo1: formData.organizationContactNo1,
        licensedProducts: formData.licensedProducts,
        referralSource: formData.referralSource || null,
        confirmDuplicate,
        teamInvites: (formData.teamInvites || [])
          .filter(r => r.email?.trim())
          .map(r => ({
            firstName:     r.firstName?.trim()     || null,
            lastName:      r.lastName?.trim()      || null,
            domain:        r.domain?.trim()        || null,
            contactNumber: r.contactNumber?.trim() || null,
            email:         r.email.trim().toLowerCase(),
            role:          r.role || 'ADMIN',
          })),
      };

      await axios.post(`${API_BASE_URL}/Signup`, signupDto);
      setShowDuplicatePrompt(false);
      navigate('/signup/confirmation', { state: { signupSuccess: true } });
    } catch (err) {
      // Backend flagged a possible duplicate org and wants the user to confirm.
      if (err.response?.status === 409 && err.response?.data?.duplicateWarning) {
        setDuplicateMessage(err.response.data.message);
        setDuplicatePromptMode('submit');
        setShowTerms(false);
        setShowDuplicatePrompt(true);
      } else {
        setShowDuplicatePrompt(false);
        setSubmitError(err.response?.data?.message || 'Signup failed. Please try again.');
        setShowTerms(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleTermsAccepted = () => submitSignup(duplicateConfirmed);

  const isLastStep = !getNextStep(currentStepKey);
  const isFirstStep = !getPreviousStep(currentStepKey);

  if (dataLoading) {
    return (
      <div className="signup-light min-h-screen bg-[#fafafa] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="signup-light min-h-screen bg-[#fafafa] flex items-center justify-center px-4">
        <div className="bg-white p-10 rounded-[20px] shadow-[0_2px_16px_rgba(0,0,0,0.10)] text-center max-w-sm w-full">
          <p className="text-[#1d1d1f] font-medium mb-2">Unable to load</p>
          <p className="text-[#6e6e73] text-sm mb-5">{dataError}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-[#0071e3] text-sm font-medium hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Possible-duplicate confirmation overlay
  if (showDuplicatePrompt) {
    return (
      <div className="signup-light min-h-screen bg-[#fafafa] flex items-center justify-center px-4 py-10">
        <div className="bg-white p-10 rounded-[20px] shadow-[0_2px_16px_rgba(0,0,0,0.10)] text-center max-w-md w-full">
          <p className="text-[#1d1d1f] font-medium mb-2">Possible duplicate organization</p>
          <p className="text-[#6e6e73] text-sm mb-6">
            {duplicateMessage ||
              'It looks like someone may have already registered your organization. Would you like to continue?'}
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => { setShowDuplicatePrompt(false); }}
              disabled={submitting}
              className="px-5 py-2 text-sm font-medium text-[#1d1d1f] bg-transparent border border-[#d2d2d7] rounded-full hover:bg-[#f5f5f7] transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDuplicateContinue}
              disabled={submitting}
              className="px-5 py-2 text-sm font-medium text-white bg-[#0071e3] rounded-full hover:bg-[#0077ed] transition-all disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // T&C acceptance overlay
  if (showTerms) {
    return (
      <div className="signup-light min-h-screen bg-[#fafafa] flex items-center justify-center px-4 py-10">
        <SignupTermsAcceptance
          onAccept={handleTermsAccepted}
          onDecline={() => setShowTerms(false)}
          loading={submitting}
        />
      </div>
    );
  }

  // Render current step component
  const renderStep = () => {
    switch (currentStepKey) {
      case 'organization':
        return (
          <OrganizationStep
            formData={formData}
            onChange={handleChange}
            onBatchChange={handleBatchChange}
            errors={errors}
            regions={regions}
            onRegisterAsyncValidate={(fn) => { orgStepAsyncValidateRef.current = fn; }}
          />
        );
      case 'contactInfo':
        return (
          <ContactInfoStep
            formData={formData}
            onChange={handleChange}
            onEmailBlur={handleEmailBlur}
            errors={errors}
          />
        );
      case 'industryDetails':
        return (
          <IndustryDetailsStep
            formData={formData}
            onChange={handleChange}
            errors={errors}
            verticals={verticals}
            subVerticals={subVerticals}
          />
        );
      case 'teamSetup':
        return (
          <TeamSetupStep
            formData={formData}
            onInviteChange={handleInviteChange}
            onAddInvite={handleAddInvite}
            onRemoveInvite={handleRemoveInvite}
            onSkip={handleSkipTeam}
            errors={errors}
          />
        );
      case 'riskModules':
        return (
          <RiskModulesStep
            formData={formData}
            onChange={handleChange}
            onCheckboxChange={handleCheckboxChange}
            onRefreshCaptcha={handleRefreshCaptcha}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="signup-light min-h-screen bg-[#fafafa] flex items-center justify-center px-4 py-10">
      <div className="bg-white w-full max-w-[560px] rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.09)] relative">

        {/* Close button */}
        <button
          type="button"
          onClick={() => { window.location.href = '/'; }}
          className="absolute top-4 right-4 text-[#86868b] hover:text-[#1d1d1f] transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f5f5f7]"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Branding header */}
        <div className="pt-10 pb-7 px-10 text-center">
          <div className="inline-flex items-center justify-center w-11 h-11 bg-[#0071e3] rounded-[14px] mb-4 shadow-[0_4px_14px_rgba(0,113,227,0.35)]">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-[22px] font-semibold text-[#1d1d1f] tracking-[-0.3px]">
            Create your account
          </h1>
          <p className="text-[14px] text-[#6e6e73] mt-1">
            Join Axaris AI - Predictive Risk Intelligence 
          </p>
        </div>

        {/* Step indicator */}
        <div className="px-10 pb-6">
          <SignupStepHeader steps={SIGNUP_STEPS} currentStepKey={currentStepKey} />
        </div>

        {/* Divider */}
        <div className="h-px bg-[#f0f0f0]" />

        {/* Form content */}
        <div className="px-10 py-7">
          {renderStep()}
        </div>

        {/* Submit error */}
        {submitError && (
          <div className="mx-10 -mt-2 mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
            <p className="text-red-600 text-sm text-center">{submitError}</p>
          </div>
        )}

        {/* Navigation */}
        <div className="px-10 pb-10">
          <div className={`grid gap-3 ${isFirstStep ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {!isFirstStep && (
              <button
                type="button"
                onClick={handleBack}
                className="py-3 rounded-xl text-[#1d1d1f] text-sm font-medium bg-[#f5f5f7] hover:bg-[#e8e8ed] transition-colors"
              >
                Back
              </button>
            )}

            {isLastStep ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="py-3 rounded-xl text-white text-[14px] font-medium bg-[#0071e3] hover:bg-[#0077ed] active:bg-[#006edb] disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Creating account…' : 'Create account'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={validating}
                className="py-3 rounded-xl text-white text-[14px] font-medium bg-[#0071e3] hover:bg-[#0077ed] active:bg-[#006edb] disabled:opacity-50 transition-colors"
              >
                {validating ? 'Validating…' : 'Continue'}
              </button>
            )}
          </div>

          {currentStepKey === 'teamSetup' && (
            <p className="text-center mt-3">
              <button
                type="button"
                onClick={handleSkipTeam}
                className="text-[13px] text-[#6e6e73] hover:text-[#1d1d1f] transition-colors"
              >
                Skip for now — I'll add team members later
              </button>
            </p>
          )}

          <p className="text-center text-[13px] text-[#6e6e73] mt-5">
            Already have an account?{' '}
            <Link to="/login" className="text-[#0071e3] hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
};

export default Signup;
